import { ReactNode } from 'react'

import { useController, useFormContext } from 'react-hook-form'
import { GoPay } from '@sga/shopify-payments-types'

import { Card, ChoiceList, Link, Banner, Stack, TextContainer, Button } from '@shopify/polaris'

import { useAccount } from '../hooks/useAccount'
import { useAuth } from './AuthContext'
import { getTranslation, getAppLocale } from '../util/getTranslation'

const APP_STORE_LINKS = {
  main: 'https://apps.shopify.com/gopay-payments',
  card: 'https://apps.shopify.com/gopay-card-payments',
  bank: 'https://apps.shopify.com/gopay-bank-payments',
} as const

type TAppStatus = { cardPayment: boolean, bankPayment: boolean }
interface GoPayDefaultInstrumentProps {
  appStatus: TAppStatus;
}

function handleOpenApp (appLink: string) {
  window.open(appLink, '_blank', 'noopener,noreferrer');
};

// Helper function to get localized help paths
function getLocalizedHelpPath(type: 'general' | 'card' | 'bank'): string {
  const locale = getAppLocale()
  
  if (type === 'general') {
    return locale === 'cs' || locale === 'cs-CZ' ? 'cs/s/meY' : 'en/s/me2'
  } else if (type === 'card') {
    return locale === 'cs' || locale === 'cs-CZ' ? 'cs/online-platba-kartou' : 'en/online-card-payments'
  } else { // bank
    return locale === 'cs' || locale === 'cs-CZ' ? 'cs/online-bankovni-prevod' : 'en/online-bank-transfer'
  }
}

export function GoPayDefaultInstrument({ appStatus }: GoPayDefaultInstrumentProps) {
  const account = useAccount<GoPay.TSegmentAccount>()
  const { appType } = useAuth()
  const { setValue } = useFormContext()
  const { field } = useController({
    name: 'defaultPaymentInstrument',
    defaultValue: account.segmentConfig.defaultPaymentInstrument || '',
  })

  function GoPayDefaultView () {
    return (
      <>
        <Banner status="info">
          {getTranslation('gopay.choose_instruction')}{' '}
          <Link external url={`https://help.gopay.com/${getLocalizedHelpPath('general')}`}>
            {getTranslation('common.instructions')}
          </Link>.
        </Banner>
        <br/>
        <ChoiceList
          selected={[field.value]}
          title={getTranslation('gopay.preferred_payment_method')}
          choices={getInstruments(appStatus, field.value).map(({ label, value, help }) => ({
            label,
            value,
            helpText: help,
          }))}
          onChange={(selected) =>
            setValue(field.name, selected[0], {
              shouldDirty: true,
              shouldTouch: true,
            })
          }
        />
      </>
    )
  }

  function GoPayAppView ({ appAInfo, appBInfo}: { appAInfo: PaymentAppInfo, appBInfo: PaymentAppInfo }) {
    return (
      <>
        <TextContainer>
          GoPay Payment Options
        </TextContainer>
        <br />
        <AppSectionView appInfo={appAInfo}/>
        <br />
        <AppSectionView appInfo={appBInfo}/>
      </>
    ) 
  }

  function AppSectionView ({ appInfo }: { appInfo: PaymentAppInfo }) {
    return (
      <Stack distribution="equalSpacing" alignment='center'>
        <TextContainer>
          {appInfo.name}
          <p>
            <Link external url={`https://www.gopay.com/${appInfo.helpPath}`}>
              {getTranslation('gopay.more_information')}
            </Link>
          </p>
        </TextContainer>
        <Button onClick={() => handleOpenApp(appInfo.appLink)}>{getTranslation('gopay.open')}</Button>
      </Stack>
    )
  }

  function selectView () {
    switch (appType) {
      case 'PAYMENT_CARD':
        return <GoPayAppView appAInfo={appInfo().gopay} appBInfo={appInfo().gopayBank}/>
      case 'BANK_ACCOUNT':
        return <GoPayAppView appAInfo={appInfo().gopay} appBInfo={appInfo().gopayCard}/>
      default:
        return <GoPayDefaultView />
    }
  }

  return (
    <Card.Section>
      {selectView()}
    </Card.Section>
  )
}

function appInfo() {
  return {
    gopay: {
      name: getTranslation('gopay.payments'),
      appLink: APP_STORE_LINKS.main,
      helpPath: getLocalizedHelpPath('general')
    },
    gopayCard: {
      name: getTranslation('gopay.card_payments'),
      appLink: APP_STORE_LINKS.card,
      helpPath: getLocalizedHelpPath('card')
    },
    gopayBank: {
      name: getTranslation('gopay.bank_payments'),
      appLink: APP_STORE_LINKS.bank,
      helpPath: getLocalizedHelpPath('bank')
    }
  }
}

type PaymentAppInfo = {
  name: string;
  appLink: string;
  helpPath: string;
}

type TInstrument = {
  value: GoPay.TPaymentInstrument
  label: string
  helpPath?: string
  help?: ReactNode
}

function getInstruments(appStatus: TAppStatus, type: string): ReadonlyArray<TInstrument> {
  const installedBanner = (
    <Banner status="warning">
      {getTranslation('gopay.duplicate_payment_warning')}
    </Banner>
  );

  const createHelpContent = (url: string, appLink: string) => (
    <Stack distribution="equalSpacing" alignment='center'>
      <TextContainer>
        <p>
          <Link external url={url}>
            {getTranslation('gopay.more_information')}
          </Link>
        </p>
      </TextContainer>
      <Button onClick={() => handleOpenApp(appLink)}>{getTranslation('gopay.open')}</Button>
    </Stack>
  );

  return [
    {
      value: '' as any,
      label: getTranslation('gopay.customer_will_choose'),
      help: (
        <span>
          {getTranslation('gopay.customer_choice_help')}
        </span>
      ),
    },
    {
      value: 'PAYMENT_CARD',
      label: getTranslation('gopay.card_payment'),
      help: appStatus.cardPayment && type === 'PAYMENT_CARD' ? 
        installedBanner : 
        createHelpContent(`https://www.gopay.com/${getLocalizedHelpPath('card')}`, APP_STORE_LINKS.card)
    },
    {
      value: 'BANK_ACCOUNT',
      label: getTranslation('gopay.bank_transfer'),
      help: appStatus.bankPayment && type === 'BANK_ACCOUNT' ? 
        installedBanner : 
        createHelpContent(`https://www.gopay.com/${getLocalizedHelpPath('bank')}`, APP_STORE_LINKS.bank)
    }
  ]
};